import { useState, useCallback, useRef } from "react";
import {
  copySchedulerEventRange,
  getEventListForAssigner,
} from "repositories/scheduler-repository";
import { handleError, handleSuccess } from "utils";
import {
  buildCopyPreviousPayload,
  collectCopyableItemsFromListData,
  getCopyItemCounts,
  getPreviousPeriodDateRange,
} from "./eventsWorkspaceCopy.utils";
import { resolveDeleteRangeIsShift } from "./eventsWorkspaceClear.utils";

/**
 * Global Copy Previous Day/Week/Month flow for Event Workspace.
 * Loads previous-period items for confirmation counts, then calls
 * SchedulerEvent/CopyRange so copies are created as drafts in the
 * currently selected period (existing records unchanged).
 * @param {{
 *   t: Function,
 *   calendarViewId: string,
 *   currentDate: Date,
 *   ownerId: number|null|undefined,
 *   eventShiftFilter?: string[],
 *   publishedDraftFilter?: string[],
 *   isEnterpriseFilterActive?: boolean,
 *   officeIds?: number[],
 *   userIds?: number[],
 *   profileId?: number|null,
 *   onRefresh?: () => void,
 * }} options
 */
export function useEventsWorkspaceCopy({
  t,
  calendarViewId,
  currentDate,
  ownerId,
  eventShiftFilter = [],
  publishedDraftFilter = [],
  isEnterpriseFilterActive = false,
  officeIds = [],
  userIds = [],
  profileId = null,
  onRefresh,
}) {
  const [confirmModal, setConfirmModal] = useState(null);
  const [isCopying, setIsCopying] = useState(false);
  const [isPreparing, setIsPreparing] = useState(false);
  const isCopyingRef = useRef(false);
  const isPreparingRef = useRef(false);

  const closeConfirmModal = useCallback(() => {
    if (isCopyingRef.current) return;
    setConfirmModal(null);
  }, []);

  const executeCopy = useCallback(async () => {
    if (!ownerId || isCopyingRef.current || !confirmModal) return;

    const itemsToCopy = confirmModal.items || [];
    isCopyingRef.current = true;
    setIsCopying(true);
    try {
      const payload = buildCopyPreviousPayload({
        ownerId,
        calendarViewId,
        currentDate,
        copyableItems: itemsToCopy,
        eventShiftFilter,
        officeIds,
        userIds,
      });
      // Use IsShift snapshotted when the confirm modal opened (matches modal counts).
      payload.IsShift = confirmModal.isShift;
      await copySchedulerEventRange(payload);
      handleSuccess(t("scheduler.copyPreviousSuccess"));
      setConfirmModal(null);
      onRefresh?.();
    } catch (error) {
      handleError(error?.message || error);
    } finally {
      isCopyingRef.current = false;
      setIsCopying(false);
    }
  }, [
    ownerId,
    confirmModal,
    calendarViewId,
    currentDate,
    eventShiftFilter,
    officeIds,
    userIds,
    t,
    onRefresh,
  ]);

  const openCopyPreviousConfirm = useCallback(async () => {
    if (!ownerId || isCopyingRef.current || isPreparingRef.current) return;

    const { startDate, endDate } = getPreviousPeriodDateRange(
      calendarViewId,
      currentDate,
    );
    if (!startDate || !endDate) return;

    isPreparingRef.current = true;
    setIsPreparing(true);
    try {
      const response = await getEventListForAssigner(
        ownerId,
        startDate,
        endDate,
        officeIds,
        userIds,
      );
      const copyableItems = collectCopyableItemsFromListData(
        response?.data || [],
        {
          eventShiftFilter,
          publishedDraftFilter,
          isEnterpriseFilterActive,
          profileId,
        },
      );

      if (!copyableItems.length) {
        handleError(t("scheduler.copyPreviousEmpty"));
        return;
      }

      const counts = getCopyItemCounts(copyableItems);
      setConfirmModal({
        items: copyableItems,
        isShift: resolveDeleteRangeIsShift({
          clearableItems: copyableItems,
          eventShiftFilter,
        }),
        ...counts,
      });
    } catch (error) {
      handleError(error?.message || error);
    } finally {
      isPreparingRef.current = false;
      setIsPreparing(false);
    }
  }, [
    ownerId,
    calendarViewId,
    currentDate,
    officeIds,
    userIds,
    eventShiftFilter,
    publishedDraftFilter,
    isEnterpriseFilterActive,
    profileId,
    t,
  ]);

  const confirmCopy = useCallback(() => {
    if (!confirmModal || isCopyingRef.current) return;
    executeCopy();
  }, [confirmModal, executeCopy]);

  return {
    isCopying,
    isPreparing,
    canCopyPrevious: Boolean(ownerId) && !isCopying && !isPreparing,
    confirmModal,
    openCopyPreviousConfirm,
    closeConfirmModal,
    confirmCopy,
  };
}
